import * as _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { SNIPPET_DETECTION, VENDORED_DEPENDENCY_DETECTION } from '../../../../../shared/featureFlags/flagNames';
import { useAddToast } from '../../../../contexts/ToastContext';
import { errorMessagesListFromResponse } from '../../../../helpers/errors';
import { useAsyncValue } from '../../../../hooks';
import { patch } from '../../../../redux/api';
import { LabeledCheckbox } from '../../../generic/Forms';
import FeatureFlagRequired from '../../../generic/Gates/FeatureFlagRequired';
import PropagateModal from '../../shared/SettingsPropagateModal';
import type { QuickImportProjectScanMethodsSettings } from '../../../../../shared/settings/project/scanMethods';
import type { FailedFetchResponse } from '../../../../helpers/errors';

type Props = {
  isOpen: boolean;
  settings: QuickImportProjectScanMethodsSettings;
  onHide: () => void;
  endpoint: string;
};

const resetCheckboxState = {
  projectDefaultQuickImportVendoredDetectionEnabled: false,
  projectDefaultQuickImportSnippetDetectionEnabled: false,
};

const usePropagateQuickImportScanMethodsSettings = ({ isOpen, onHide, endpoint }: Omit<Props, 'settings'>) => {
  const addToast = useAddToast();
  const asyncValue = useAsyncValue<void>();
  const [checkboxes, setCheckboxes] = useState<
    Record<keyof QuickImportProjectScanMethodsSettings, boolean>
  >(resetCheckboxState);

  useEffect(() => {
    setCheckboxes(resetCheckboxState);
  }, [isOpen]);

  const onSavePropagateSettings = useCallback(async () => {
    try {
      asyncValue.setPending();
      await patch<Array<keyof QuickImportProjectScanMethodsSettings>>(
        endpoint,
        _.compact(
          Object.entries(checkboxes)
            .map(([key, value]) => (value ? (key as keyof QuickImportProjectScanMethodsSettings) : null)),
        ),
      );
      asyncValue.setResolve();
      addToast({ type: 'success', content: 'Settings successfully applied to all projects.' });
      onHide();
    } catch (error) {
      asyncValue.setReject(errorMessagesListFromResponse(error as FailedFetchResponse));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addToast, asyncValue, checkboxes, onHide]);

  return {
    checkboxes,
    setCheckboxes,
    onSavePropagateSettings,
    isLoading: asyncValue.isLoading,
    errors: asyncValue.errors,
    isSaveButtonDisabled: asyncValue.isLoading || _.isEqual(checkboxes, resetCheckboxState),
  };
};

const PropagateScanMethodsModal: VoidFunctionComponent<Props> = ({
  isOpen,
  settings,
  onHide,
  endpoint,
}) => {
  const { errors, isLoading, checkboxes, setCheckboxes, isSaveButtonDisabled, onSavePropagateSettings } =
    usePropagateQuickImportScanMethodsSettings({ isOpen, onHide, endpoint });

  return (
    <PropagateModal
      settingsCategory="scan methods"
      isOpen={isOpen}
      errors={errors}
      onHide={onHide}
      onSave={onSavePropagateSettings}
      isSaveButtonDisabled={isSaveButtonDisabled}
      isSaveButtonLoading={isLoading}
    >
      <FeatureFlagRequired flagName={VENDORED_DEPENDENCY_DETECTION}>
        <LabeledCheckbox
          label={
            settings.projectDefaultQuickImportVendoredDetectionEnabled
              ? 'Enable vendored dependency scanning for all projects'
              : 'Disable vendored dependency scanning for all projects'
          }
          isChecked={checkboxes.projectDefaultQuickImportVendoredDetectionEnabled}
          onChange={(_event, isChecked) =>
            setCheckboxes((current) => ({
              ...current,
              projectDefaultQuickImportVendoredDetectionEnabled: isChecked,
            }))}
          isRippleDisabled
        />
      </FeatureFlagRequired>
      <FeatureFlagRequired flagName={SNIPPET_DETECTION}>
        <LabeledCheckbox
          label={
            settings.projectDefaultQuickImportSnippetDetectionEnabled
              ? 'Enable snippet scanning for all projects'
              : 'Disable snippet scanning for all projects'
          }
          isChecked={checkboxes.projectDefaultQuickImportSnippetDetectionEnabled}
          onChange={(_event, isChecked) =>
            setCheckboxes((current) => ({
              ...current,
              projectDefaultQuickImportSnippetDetectionEnabled: isChecked,
            }))}
          isRippleDisabled
        />
      </FeatureFlagRequired>
    </PropagateModal>
  );
};

export default PropagateScanMethodsModal;
